// src/app/core/verifiable-credential.service.ts
import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { NodeShapeDTO } from '../models/ShaclShapeSummary';
import { VCGenerationConfig, VCGenerationResult, VerifiableCredential, CredentialSubject } from '../models/verifiable-credential.types';
import { API_BASE_SDSERVICE } from 'src/environments/api';


@Injectable({ providedIn: 'root' })
export class VerifiableCredentialService {
    // Default GaiaX contexts
    private readonly DEFAULT_CONTEXTS = [
        'https://www.w3.org/ns/credentials/v2',
        'https://w3id.org/gaia-x/development'
    ];

    // Default credential types
    private readonly DEFAULT_TYPES = ['VerifiableCredential', 'GaiaXCredential'];

    // Default issuer DID (should be configured per deployment)
    private readonly DEFAULT_ISSUER_DID = 'did:web:wizard.lab.pb40.eu:api:credentials:xx';

    private readonly base = API_BASE_SDSERVICE;

    constructor(private http: HttpClient) { }

    /**
     * Generate a W3C Verifiable Credential from form data
     * @param formData The form values from the SHACL-based form
     * @param shape The SHACL shape definition used to generate the form
     * @param config Optional configuration for VC generation
     * @returns VCGenerationResult with the generated credential or error
     */
    generateCredential(
        formData: any,
        shape: NodeShapeDTO,
        config?: VCGenerationConfig
    ): VCGenerationResult {
        try {
            // Build the credential subject from form data
            const credentialSubject = this.buildCredentialSubject(
                formData,
                shape.targetClass,
                shape.id,
                this.generateTermsAndConditionsId(),
            );

            if (!credentialSubject.id) {
                throw new Error('Credential Subject ID is required');
            }

            const issuanceDate = new Date().toISOString();
            const validFrom = issuanceDate;

            const credential: VerifiableCredential = {
                '@context': [
                    'https://www.w3.org/2018/credentials/v1',
                    'https://w3id.org/security/suites/jws-2020/v1',
                    'https://registry.lab.gaia-x.eu/development/api/trusted-shape-registry/v1/shapes/jsonld/trustframework#'
                ],
                type: ['VerifiableCredential'],
                id: this.generateCredentialId(),
                issuer: config?.issuerDid || this.DEFAULT_ISSUER_DID,
                issuanceDate: issuanceDate,
                validFrom: validFrom,
                credentialSubject
            };

            return {
                success: true,
                credential
            };
        } catch (error) {
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }

    /**
     * Save form data to backend for mapping and persistence
     */
    saveToBackend(params: {
        userId?: number;
        orgId?: number;
        name: string;
        description: string;
        targetClass: string;
        shapeId: string;
        formData: any;
    }): Observable<any> {
        const url = `${this.base}/api/sd/saved`;
        return this.http.post<any>(url, params);
    }

    // Mappings for Gaia-X compliance
    private readonly PROPERTY_MAPPINGS: { [key: string]: string } = {
        'name': 'gx:legalName',
        'schema:name': 'gx:legalName',
        'description': 'gx:description',
        'schema:description': 'gx:description',
        'headquartersAddress': 'gx:headquarterAddress',
        'gx:headquartersAddress': 'gx:headquarterAddress', // Fix plural/singular mismatch
        'registrationNumber': 'gx:legalRegistrationNumber',
        'gx:registrationNumber': 'gx:legalRegistrationNumber',
        'legalAddress': 'gx:legalAddress',
        'parentOrganizationOf': 'gx:parentOrganizationOf',
        'subOrganisationOf': 'gx:subOrganisationOf'
    };

    private readonly TYPE_MAPPINGS: { [key: string]: string } = {
        'LegalPerson': 'gx:LegalParticipant',
        'https://w3id.org/gaia-x/development#LegalPerson': 'gx:LegalParticipant'
    };

    /**
     * Build credential subject from form data
     * Maps form fields to credential subject properties
     */
    private buildCredentialSubject(
        formData: any,
        targetClass: string,
        shapeId?: string,
        gaiaXTermsAndConditions?: string
    ): CredentialSubject {
        // Map the type if exists
        const mappedType = this.mapType(targetClass);

        const subject: CredentialSubject = {
            type: mappedType,
            'gx-terms-and-conditions:gaiaxTermsAndConditions': gaiaXTermsAndConditions
        };

        // Process and clean the form data
        const processedData = this.processFormData(formData);

        // Add all processed data as properties
        Object.keys(processedData).forEach(key => {
            const value = processedData[key];
            if (this.isValidValue(value)) {
                subject[key] = value;
            }
        });

        // Generate a subject ID if not provided in form data
        if (!subject.id) {
            subject.id = this.generateSubjectId();
        }

        return subject;
    }

    /**
     * Map type to Gaia-X compliant type name
     */
    private mapType(type: string): string {
        // Check exact match
        if (this.TYPE_MAPPINGS[type]) {
            return this.TYPE_MAPPINGS[type];
        }

        // Check if it ends with a known type
        const parts = type.split('#');
        const lastPart = parts[parts.length - 1];
        if (this.TYPE_MAPPINGS[lastPart]) {
            return this.TYPE_MAPPINGS[lastPart];
        }

        return type;
    }

    /**
     * Process form data to extract meaningful values
     * Handles the complex nested structure from form-field component
     */
    private processFormData(formData: any, parentKey: string = ''): any {
        const result: any = {};

        if (!formData || typeof formData !== 'object') {
            return formData;
        }

        // Handle arrays
        if (Array.isArray(formData)) {
            const processedArray = formData
                .map(item => this.processFormData(item, parentKey))
                .filter(item => this.isValidValue(item));

            return processedArray.length > 0 ? processedArray : undefined;
        }

        // Handle objects
        Object.keys(formData).forEach(key => {
            const value = formData[key];

            // Extract the property name from the form control key
            let propertyName = this.extractPropertyName(key);

            // Apply mapping
            if (this.PROPERTY_MAPPINGS[propertyName]) {
                propertyName = this.PROPERTY_MAPPINGS[propertyName];
            } else if (propertyName.startsWith('gx:') && this.PROPERTY_MAPPINGS[propertyName.substring(3)]) {
                // Check mapping without prefix
                propertyName = this.PROPERTY_MAPPINGS[propertyName.substring(3)];
            }

            if (value !== null && value !== undefined) {
                // Handle nested objects
                if (typeof value === 'object' && !Array.isArray(value)) {
                    const processed = this.processFormData(value, propertyName);
                    if (processed && Object.keys(processed).length > 0) {
                        // Keep nested structure!
                        result[propertyName] = processed;
                    }
                }
                // Handle arrays
                else if (Array.isArray(value)) {
                    const processedArray = value
                        .map(item => this.processFormData(item, propertyName))
                        .filter(item => this.isValidValue(item));

                    if (processedArray.length > 0) {
                        result[propertyName] = processedArray;
                    }
                }
                // Handle primitive values
                else if (value !== '') {
                    result[propertyName] = value;
                }
            }
        });

        return result;
    }

    /**
     * Extract property name from form control key
     * Converts encoded keys back to proper GaiaX property names
     * 
     * Examples:
     * - "https___w3id_org_gaia_x_development_name" -> "gx:name"
     * - "https___w3id_org_gaia_x_development_legaladdress" -> "gx:legalAddress"
     * - "headquarters_address_https_w3id_org_gaia_x_development_countryname" -> "gx:countryName"
     */
    private extractPropertyName(key: string): string {
        // If key doesn't contain underscores, return as-is
        if (!key.includes('_')) {
            return key;
        }

        // Check if this is a GaiaX property (contains gaia-x URL pattern)
        if (key.includes('gaia') && key.includes('x')) {
            // Extract the property name after the last occurrence of development, core, or participant
            const patterns = ['development', 'core', 'participant'];

            for (const pattern of patterns) {
                const patternIndex = key.lastIndexOf(pattern);
                if (patternIndex !== -1) {
                    // Get everything after the pattern and the following underscore
                    const afterPattern = key.substring(patternIndex + pattern.length + 1);

                    // Clean up the property name
                    let propertyName = afterPattern
                        .split('_')
                        .filter(p => p.length > 0)
                        .join('');

                    // Convert to camelCase if needed
                    propertyName = this.toCamelCase(propertyName);

                    // Add gx: prefix for GaiaX properties
                    return `gx:${propertyName}`;
                }
            }
        }

        // For non-GaiaX properties, extract the last meaningful segment
        const parts = key.split('_').filter(p => p.length > 0);
        const lastPart = parts[parts.length - 1] || key;

        return this.toCamelCase(lastPart);
    }

    /**
     * Convert string to camelCase
     */
    private toCamelCase(str: string): string {
        if (!str) return str;

        // If already camelCase or has mixed case, return as-is
        if (str !== str.toLowerCase() && str !== str.toUpperCase()) {
            return str;
        }

        // Convert to camelCase
        return str.charAt(0).toLowerCase() + str.slice(1);
    }

    /**
     * Check if a value is valid (not empty, null, or undefined)
     */
    private isValidValue(value: any): boolean {
        if (value === null || value === undefined || value === '') {
            return false;
        }

        if (Array.isArray(value)) {
            return value.length > 0 && value.some(item => this.isValidValue(item));
        }

        if (typeof value === 'object') {
            return Object.keys(value).length > 0;
        }

        return true;
    }

    /**
     * Generate a unique credential ID using UUID v4 format
     */
    private generateCredentialId(): string {
        return `urn:uuid:${this.generateUUID()}`;
    }


    /**
 * Generate a unique terms and conditions ID using UUID v4 format
 */
    private generateTermsAndConditionsId(): string {
        return `${this.generateUUID()}`;
    }

    /**
     * Generate a unique subject ID
     */
    private generateSubjectId(): string {
        return `urn:uuid:${this.generateUUID()}`;
    }

    /**
     * Generate a UUID v4
     * Simple implementation without external dependencies
     */
    private generateUUID(): string {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = (Math.random() * 16) | 0;
            const v = c === 'x' ? r : (r & 0x3) | 0x8;
            return v.toString(16);
        });
    }

    /**
     * Calculate validUntil timestamp based on duration in days
     */
    private calculateValidUntil(durationDays: number): string {
        const date = new Date();
        date.setDate(date.getDate() + durationDays);
        return date.toISOString();
    }

    /**
     * Validate a credential structure (basic validation)
     * @param credential The credential to validate
     * @returns true if valid, false otherwise
     */
    validateCredentialStructure(credential: VerifiableCredential): boolean {
        try {
            // Check required fields
            if (!credential['@context'] || !credential.type || !credential.id) {
                return false;
            }

            if (!credential.issuer || !credential.validFrom) {
                return false;
            }

            if (!credential.credentialSubject) {
                return false;
            }

            // Check that @context includes W3C VC context
            const contexts = Array.isArray(credential['@context'])
                ? credential['@context']
                : [credential['@context']];

            if (!contexts.includes('https://www.w3.org/ns/credentials/v2')) {
                return false;
            }

            // Check that type includes VerifiableCredential
            const types = Array.isArray(credential.type)
                ? credential.type
                : [credential.type];

            if (!types.includes('VerifiableCredential')) {
                return false;
            }

            return true;
        } catch {
            return false;
        }
    }

    /**
     * Convert credential to JSON string with pretty formatting
     */
    credentialToJson(credential: VerifiableCredential, pretty = true): string {
        return JSON.stringify(credential, null, pretty ? 2 : 0);
    }
}
